import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    GiftOutlined,
    LockOutlined,
    MailOutlined,
    QuestionCircleOutlined,
    SafetyOutlined,
    SettingOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Modal, Spin, Table, Typography } from 'antd';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '~components/button';
import { NotificationManager } from '~components/notification-manager';
import { Tag, TagVariant } from '~components/tag';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useClientStore,
} from '~contexts/mobx';
import {
    ApplicationSelection,
    ApplicationTargetEnum,
} from '~types/app-selection';
import style from './style.module.scss';

const { Text } = Typography;

interface OnboardingModalProps {
    isVisible: boolean;
    onClose: () => void;
    clientId?: number;
}

interface OnboardingStep {
    stepDoneAt?: string;
    action: string;
    stepStatus: string;
    userResponse?: string | null;
    id: number;
    stepAssociatedApp?: string;
}

const formatLabel = (label: string) =>
    label.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const getAppLabel = (appCode: string): string => {
    if (appCode === ApplicationTargetEnum.QC) return 'Couche Tard';
    if (appCode === ApplicationTargetEnum.CA) return 'Circle K';
    return appCode;
};

const getStepStatusVariant = (status: string): TagVariant => {
    switch (status.toUpperCase()) {
        case 'COMPLETED':
            return 'success';
        case 'BYPASSED':
            return 'orange';
        case 'IN_PROGRESS':
            return 'blue';
        case 'PENDING':
        case 'NOT_STARTED':
        default:
            return 'grey';
    }
};

const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
        case 'user_segmentation_setup':
            return <UserOutlined />;
        case 'profile_setup':
            return <SettingOutlined />;
        case 'account_setup':
            return <MailOutlined />;
        case 'permissions_setup':
            return <LockOutlined />;
        case 'phone_confirmation_setup':
            return <SafetyOutlined />;
        case 'congratulations_setup':
            return <GiftOutlined />;
        default:
            return <QuestionCircleOutlined />;
    }
};

const renderDetails = (response?: string | null) => {
    if (!response) {
        return <Text type="secondary">---</Text>;
    }

    try {
        const parsedResponse = JSON.parse(response);
        const push =
            parsedResponse.Push !== undefined ? (
                <Tag variant={parsedResponse.Push ? 'success' : 'error'}>
                    Push: {parsedResponse.Push.toString()}
                </Tag>
            ) : null;
        const location =
            parsedResponse.Location !== undefined ? (
                <Tag variant={parsedResponse.Location ? 'success' : 'error'}>
                    Location: {parsedResponse.Location.toString()}
                </Tag>
            ) : null;

        return (
            <div style={{ display: 'flex', gap: '8px' }}>
                {push}
                {location}
            </div>
        );
    } catch {
        return <Tag variant="success">{response}</Tag>;
    }
};

export const OnboardingModal = ({
    isVisible,
    onClose,
    clientId,
}: OnboardingModalProps) => {
    const clientStore = useClientStore();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const { hasManagementAccess, isCustomerServiceAgent } = authStore;

    useEffect(() => {
        if (isVisible && clientId) {
            clientStore.fetchOnboardingSteps(clientId);
        }
    }, [isVisible, clientId, clientStore]);

    const progression = clientStore.onboardingProgression;

    const getAppProgression = (appCode: string): string =>
        progression?.[appCode] ?? 'UNKNOWN';

    const allSteps = useMemo(
        () => (clientStore.onboardingSteps?.steps ?? []) as OnboardingStep[],
        [clientStore.onboardingSteps?.steps]
    );

    // Only trust an empty step list once it is known to belong to this client.
    // The store keeps the previous client's data across a change and nulls
    // everything on a failed fetch, so without this an unloaded client reads as
    // legacy. That matters because the two actions are not equally forgiving:
    // Complete on a legacy client is refused, but Restart on a real client
    // succeeds and resets their progression.
    const isDataLoadedForClient =
        clientId != null && clientStore.onboardingStepsClientId === clientId;

    const apps = useMemo(() => {
        const appSet = new Set<ApplicationTargetEnum>();
        allSteps.forEach((step) => {
            if (step.stepAssociatedApp) {
                appSet.add(step.stepAssociatedApp as ApplicationTargetEnum);
            }
        });

        // Most Legacy users have no step records either, so the tabs would be
        // empty and Restart Onboarding could never be clicked: exactly the
        // clients the action exists for. Fall back to every app target so the
        // agent can pick.
        if (appSet.size === 0 && isDataLoadedForClient) {
            return Object.values(ApplicationTargetEnum);
        }

        return Array.from(appSet);
    }, [allSteps, isDataLoadedForClient]);

    const defaultTab = useMemo((): ApplicationTargetEnum | undefined => {
        if (apps.length === 0) return undefined;
        const { selectedApplication } = applicationSelectionStore;
        if (selectedApplication === ApplicationSelection.COUCHE_TARD) {
            return apps.includes(ApplicationTargetEnum.QC)
                ? ApplicationTargetEnum.QC
                : apps[0];
        }
        if (selectedApplication === ApplicationSelection.CIRCLE_K) {
            return apps.includes(ApplicationTargetEnum.CA)
                ? ApplicationTargetEnum.CA
                : apps[0];
        }
        return apps[0];
    }, [apps, applicationSelectionStore]);

    const [activeTab, setActiveTab] = useState<
        ApplicationTargetEnum | undefined
    >(undefined);

    useEffect(() => {
        if (isVisible && defaultTab) {
            setActiveTab(defaultTab);
        }
        if (!isVisible) {
            setActiveTab(undefined);
        }
    }, [isVisible, defaultTab]);

    const filteredSteps = useMemo(
        () => allSteps.filter((step) => step.stepAssociatedApp === activeTab),
        [allSteps, activeTab]
    );

    const activeTabProgression = activeTab
        ? getAppProgression(activeTab)
        : 'UNKNOWN';

    // Legacy is the server's definition, a Client with no progression row,
    // because that row is what Complete acts on. Step records are not enough:
    // a Client can hold them without the row, and Complete refuses them.
    const isLegacyClient =
        clientStore.onboardingSteps?.hasOnboardingProgression === false;

    const handleRestartOnboarding = async () => {
        if (!clientId) {
            NotificationManager.showError('No client selected');
            return;
        }
        if (!activeTab) {
            NotificationManager.showError('No app target selected');
            return;
        }
        // The target has to be sent: a legacy client has no progression row
        // for the server to read it from.
        await clientStore.restartOnboarding(clientId, activeTab);
    };

    const handleCompleteOnboarding = async () => {
        if (!clientId) {
            NotificationManager.showError('No client selected');
            return;
        }
        // No target: the server reads the onboarding off the client's own
        // progression row.
        await clientStore.completeOnboarding(clientId);
    };

    const isAllCompleted =
        progression != null &&
        Object.values(progression).every((v) => v === 'COMPLETED');

    // Restart is always offered to a Legacy user. Complete is for a Client stuck
    // partway, so it is offered only on a tab that reads IN_PROGRESS. The other
    // two states are refusals in disguise: COMPLETED is refused by the server,
    // which acts on the Client's newest progression row whatever its app, and
    // NOT_STARTED (what a Restart writes, and what an app with no row reads)
    // would record as finished an onboarding the Client never did.
    const isActionOffered =
        isLegacyClient || activeTabProgression === 'IN_PROGRESS';
    const showActionButton =
        (hasManagementAccess || isCustomerServiceAgent) &&
        isDataLoadedForClient &&
        !isAllCompleted &&
        isActionOffered;

    const actionButton = isLegacyClient ? (
        <Button
            key="restart"
            variant="primary"
            loading={clientStore.isRestartingOnboarding}
            onClick={handleRestartOnboarding}
            disabled={
                clientStore.isFetchingOnboardingSteps ||
                clientStore.isRestartingOnboarding
            }
            aria-label="Restart client onboarding process"
            data-testid="restart-onboarding-btn"
        >
            {clientStore.isRestartingOnboarding
                ? 'Restarting...'
                : 'Restart Onboarding'}
        </Button>
    ) : (
        <Button
            key="complete"
            variant="primary"
            loading={clientStore.isCompletingOnboarding}
            onClick={handleCompleteOnboarding}
            disabled={
                clientStore.isFetchingOnboardingSteps ||
                clientStore.isCompletingOnboarding
            }
            aria-label="Complete client onboarding process"
            data-testid="complete-onboarding-btn"
        >
            {clientStore.isCompletingOnboarding
                ? 'Completing...'
                : 'Complete Onboarding'}
        </Button>
    );

    const modalFooter = showActionButton
        ? [
              <Button key="close" variant="secondary" onClick={onClose}>
                  Close
              </Button>,
              actionButton,
          ]
        : [
              <Button key="close" variant="secondary" onClick={onClose}>
                  Close
              </Button>,
          ];

    const columns = [
        {
            title: formatLabel('date'),
            dataIndex: 'stepDoneAt',
            key: 'stepDoneAt',
            sorter: (a: OnboardingStep, b: OnboardingStep) =>
                moment(a.stepDoneAt).unix() - moment(b.stepDoneAt).unix(),
            render: (stepDoneAt?: string) =>
                stepDoneAt ? (
                    <div>
                        <Text strong style={{ fontSize: '14px' }}>
                            {moment(stepDoneAt).format('h:mm A')}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {moment(stepDoneAt).format('MMM D, YYYY')}
                        </Text>
                    </div>
                ) : (
                    'N/A'
                ),
        },
        {
            title: formatLabel('step'),
            dataIndex: 'action',
            key: 'action',
            render: (action: string) => (
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {getActionIcon(action)}
                    {formatLabel(action)}
                </span>
            ),
        },
        {
            title: formatLabel('status'),
            dataIndex: 'stepStatus',
            key: 'stepStatus',
            render: (status: string) => (
                <Tag variant={getStepStatusVariant(status)}>
                    {formatLabel(status)}
                </Tag>
            ),
        },
        {
            title: formatLabel('details'),
            dataIndex: 'userResponse',
            key: 'userResponse',
            render: renderDetails,
        },
    ];

    return (
        <Modal
            title={<Text strong>Onboarding Progression</Text>}
            open={isVisible}
            onCancel={onClose}
            footer={modalFooter}
            centered
            width={800}
            className={style.onboardingModal}
        >
            {clientStore.isFetchingOnboardingSteps ? (
                <div className={style.loadingContainer}>
                    <Spin size="large" />
                </div>
            ) : apps.length > 0 && activeTab ? (
                <div className={style.tabbedContent}>
                    <div className={style.tabHeader}>
                        <div className={style.pillTabs}>
                            {apps.map((appCode) => (
                                <button
                                    key={appCode}
                                    className={`${style.pillTab} ${
                                        activeTab === appCode
                                            ? style.pillTabActive
                                            : ''
                                    }`}
                                    onClick={() => setActiveTab(appCode)}
                                >
                                    {getAppLabel(appCode)}
                                </button>
                            ))}
                        </div>
                        <Tag
                            variant={
                                activeTabProgression === 'COMPLETED'
                                    ? 'success'
                                    : 'error'
                            }
                        >
                            {activeTabProgression === 'COMPLETED' ? (
                                <>
                                    <CheckCircleOutlined /> Completed
                                </>
                            ) : (
                                <>
                                    <ExclamationCircleOutlined /> Incomplete
                                </>
                            )}
                        </Tag>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={filteredSteps}
                        rowKey="id"
                        pagination={false}
                        className={style.onboardingTable}
                    />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={[]}
                    rowKey="id"
                    pagination={false}
                    className={style.onboardingTable}
                />
            )}
        </Modal>
    );
};

export default observer(OnboardingModal);
